(function (root, factory) {
  const api = factory(root);
  if (typeof module === 'object' && module.exports) module.exports = api;
  else api.boot();
})(typeof globalThis !== 'undefined' ? globalThis : this, function (root) {
  'use strict';
  const PAGE_SIZE = 24;
  const PREDICTIVE_SIZE = 6;
  const initialized = new WeakSet();
  let indexPromise;

  function normalize(value) {
    return String(value || '').normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase('en-US');
  }
  function tokens(value) {
    const words = normalize(value).match(/[\p{L}\p{N}]+/gu) || [];
    return [...new Set(words.map(word => word === 'axes' ? 'axis' : word))];
  }
  function safeURL(value, kind, origin) {
    if (typeof value !== 'string' || !value.trim()) return '';
    try {
      const url = new URL(String(value), origin);
      if (!['https:', 'http:'].includes(url.protocol) || url.username || url.password) return '';
      if (kind === 'page') {
        if (url.origin !== origin || !url.pathname.startsWith('/pages/')) return '';
        return url.pathname + url.search + url.hash;
      }
      if (url.protocol !== 'https:' || (url.hostname !== 'cdn.shopify.com' && url.origin !== origin)) return '';
      return url.href;
    } catch (_) { return ''; }
  }
  function prepareIndex(raw, origin) {
    if (!Array.isArray(raw)) throw new Error('Invalid machine index');
    const seen = new Set();
    return raw.filter(record => record && typeof record === 'object').map(record => {
      const url = safeURL(record.url, 'page', origin);
      const handle = typeof record.handle === 'string' ? record.handle : '';
      if (!url || !handle || seen.has(handle) || typeof record.name !== 'string') return null;
      seen.add(handle);
      const image = record.image && typeof record.image === 'object' ? record.image : {};
      const item = {
        handle, name: record.name, model: String(record.model || ''), manufacturer: String(record.manufacturer || ''), url,
        image: {url: safeURL(image.url, 'image', origin), alt: String(image.alt || record.name), width: Number(image.width) || 500, height: Number(image.height) || 500},
        terms: normalize([record.name, record.model, record.manufacturer, ...(Array.isArray(record.tags) ? record.tags : []), ...(Array.isArray(record.sku) ? record.sku : [record.sku || ''])].join(' '))
      };
      item.words = tokens(item.terms);
      return item;
    }).filter(Boolean);
  }
  function matchRecords(records, query) {
    const terms = tokens(query);
    if (!terms.length) return [];
    const phrase = terms.join(' ');
    const score = record => tokens(record.model).join(' ') === phrase ? 0 : tokens(record.name).join(' ') === phrase ? 1 : tokens(record.name).join(' ').startsWith(phrase) ? 2 : 3;
    return records.filter(record => {
      const words = record.words || tokens(record.terms);
      return terms.every(term => term.length === 1 || /^\p{N}+$/u.test(term) ? words.includes(term) : words.some(word => word.startsWith(term)));
    }).sort((a, b) => score(a) - score(b) || a.name.localeCompare(b.name, 'en', {numeric: true}));
  }
  function element(tag, className, text) {
    const node = root.document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  }
  function card(record, predictive) {
    const li = element('li', predictive ? 'centex-machine-predictive-item' : 'productgrid--item centex-machine-search-card');
    const link = element('a', predictive ? 'centex-machine-predictive-link' : 'centex-machine-search-link');
    link.href = record.url;
    if (record.image.url) {
      const image = element('img', 'centex-machine-search-image');
      image.src = record.image.url;
      image.alt = record.image.alt;
      image.width = Math.max(1, Math.min(10000, record.image.width));
      image.height = Math.max(1, Math.min(10000, record.image.height));
      image.loading = 'lazy';
      link.append(image);
    }
    const text = element('span', 'centex-machine-search-copy');
    text.append(element(predictive ? 'span' : 'h3', 'centex-machine-search-name', record.name));
    if (record.manufacturer) text.append(element('span', 'centex-machine-search-maker', record.manufacturer));
    link.append(text);
    li.append(link);
    return li;
  }
  function renderResults(host, records, query) {
    const matches = matchRecords(records, query);
    host.replaceChildren();
    host.hidden = !tokens(query).length;
    if (host.hidden) return;
    host.append(element('h2', 'centex-machine-search-heading', 'Machinery'));
    const status = element('p', 'centex-machine-search-count', matches.length ? `${matches.length} matching machine${matches.length === 1 ? '' : 's'}` : 'No machines matched this search.');
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');
    host.append(status);
    const list = element('ul', 'productgrid--items centex-machine-search-results');
    host.append(list);
    let shown = 0;
    const more = element('button', 'button-secondary centex-machine-search-more', 'Show more machines');
    more.type = 'button';
    function appendPage() {
      matches.slice(shown, shown + PAGE_SIZE).forEach(record => list.append(card(record, false)));
      shown = Math.min(shown + PAGE_SIZE, matches.length);
      more.hidden = shown >= matches.length;
      more.setAttribute('aria-label', `Show more machines (${shown} of ${matches.length} shown)`);
    }
    more.addEventListener('click', appendPage);
    host.append(more);
    appendPage();
  }
  function loadIndex(config) {
    if (!indexPromise) {
      indexPromise = root.fetch(config.index_url, {credentials: 'same-origin'}).then(response => {
        if (!response.ok) throw new Error('Machine search unavailable');
        return response.json();
      }).then(raw => prepareIndex(raw, root.location.origin)).catch(error => { indexPromise = undefined; throw error; });
    }
    return indexPromise;
  }
  function searchLink(config, query) {
    const url = new URL(config.search_url || '/search', root.location.origin);
    url.searchParams.set('q', query);
    return url.pathname + url.search;
  }
  function attachPredictive(input, config) {
    const form = input.closest('form');
    const container = input.closest('[data-live-search]');
    const nativeFlydown = container && container.querySelector('[data-live-search-flydown]');
    if (!form || !nativeFlydown || initialized.has(input)) return;
    initialized.add(input);
    const panel = element('section', 'centex-machine-predictive');
    panel.setAttribute('aria-label', 'Matching machinery');
    panel.hidden = true;
    nativeFlydown.append(panel);
    let timer;
    let version = 0;
    let skipFocus = false;
    const hide = () => { panel.hidden = true; version += 1; };
    async function update() {
      const current = ++version;
      const query = input.value.trim();
      if (!tokens(query).length) { panel.hidden = true; return; }
      try {
        const records = await loadIndex(config);
        if (current !== version || input.value.trim() !== query) return;
        const matches = matchRecords(records, query);
        panel.replaceChildren();
        panel.hidden = matches.length === 0;
        if (panel.hidden) return;
        panel.append(element('h3', 'centex-machine-search-heading', 'Machinery'));
        const list = element('ul', 'centex-machine-predictive-list');
        matches.slice(0, PREDICTIVE_SIZE).forEach(record => list.append(card(record, true)));
        panel.append(list);
        const all = element('a', 'centex-machine-predictive-all', `View all ${matches.length} matching machine${matches.length === 1 ? '' : 's'}`);
        all.href = searchLink(config, query);
        panel.append(all);
      } catch (_) { if (current === version) panel.hidden = true; }
    }
    input.addEventListener('input', () => { version += 1; root.clearTimeout(timer); timer = root.setTimeout(update, 160); });
    input.addEventListener('focus', () => { if (skipFocus) skipFocus = false; else return update(); });
    input.addEventListener('keydown', event => { if (event.key === 'Escape') hide(); });
    panel.addEventListener('keydown', event => {
      if (event.key === 'Escape') { skipFocus = true; hide(); event.stopPropagation?.(); input.focus(); return; }
      if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
      const links = [...panel.querySelectorAll('a')];
      const index = links.indexOf(root.document.activeElement);
      if (index < 0 || !links.length) return;
      event.preventDefault();
      event.stopPropagation?.();
      const next = event.key === 'Home' ? 0 : event.key === 'End' ? links.length - 1 : (index + (event.key === 'ArrowUp' ? -1 : 1) + links.length) % links.length;
      links[next].focus();
    });
    container.addEventListener('focusout', () => root.setTimeout(() => { if (!container.contains(root.document.activeElement)) hide(); }, 0));
    form.addEventListener('submit', hide);
  }
  function initialize(config) {
    root.document.querySelectorAll('[data-centex-machine-search-results]').forEach(host => {
      if (initialized.has(host)) return;
      initialized.add(host);
      const query = new URL(root.location.href).searchParams.get('q') || '';
      if (!tokens(query).length) return;
      loadIndex(config).then(records => renderResults(host, records, query)).catch(() => {
        host.hidden = false;
        host.replaceChildren(element('h2', 'centex-machine-search-heading', 'Machinery'));
        host.append(element('p', '', 'Machine search is temporarily unavailable.'));
        const browse = element('a', '', 'Browse machinery');
        browse.href = '/collections/all';
        host.append(browse);
      });
    });
    root.document.querySelectorAll('[data-live-search-form] [data-live-search-input]').forEach(input => attachPredictive(input, config));
  }
  function boot() {
    if (!root.document) return;
    function start() {
      const configNode = root.document.querySelector('[data-centex-machine-search-config]');
      if (!configNode) return;
      let config;
      try { config = JSON.parse(configNode.textContent); } catch (_) { return; }
      if (!config.index_url) return;
      initialize(config);
      root.document.addEventListener('shopify:section:load', () => initialize(config));
    }
    if (root.document.readyState === 'loading') root.document.addEventListener('DOMContentLoaded', start, {once: true});
    else start();
  }
  return {normalize, tokens, safeURL, prepareIndex, matchRecords, card, renderResults, attachPredictive, boot};
});
